/**
 * SoundLoadError — "we couldn't load the sounds" (P23/CC-60, invariant I3).
 *
 * Mini-games that do not mount `InstrumentStage` (count and play, who
 * sounds?) used to preload their samples with a `void (async () => …)()` and no
 * `catch`: if the download failed, the `ready` signal stayed `false` FOREVER
 * and the whole game rendered with every button disabled and no message. Not
 * loading, not an error, not empty: a dead, silent game, indistinguishable to
 * a 2-6-year-old from "the app is broken".
 *
 * This is the third missing state: explicit, with a retry that really retries.
 * It deliberately does NOT resemble the game's normal state (sad mascot +
 * alert border), so it cannot be mistaken for "still loading".
 */

import { RotateCcw } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

interface SoundLoadErrorProps {
  /** `data-testid` prefix (identifies the screen in probes). */
  testId: string;
  onRetry: () => void;
}

export function SoundLoadError(props: SoundLoadErrorProps) {
  const { t } = useI18n();
  return (
    <div
      data-testid={`${props.testId}-sound-error`}
      role="alert"
      class="mx-auto flex max-w-sm flex-col items-center gap-4 rounded-3xl border-4 border-destructive/40 bg-surface/90 p-6 text-center"
    >
      <span class="text-[clamp(3rem,14vmin,4.5rem)]" aria-hidden="true">
        🔇
      </span>
      <p class="text-lg font-bold text-foreground">{t(msg`We couldn't load the sounds`)}</p>
      <p class="text-base text-muted">{t(msg`Check the connection and try again.`)}</p>
      <button
        type="button"
        data-testid={`${props.testId}-sound-retry`}
        onClick={() => {
          props.onRetry();
        }}
        class="tile-3d tile-3d-press flex min-h-16 items-center gap-3 rounded-full bg-joy-grass px-8 text-lg font-bold text-emerald-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring"
      >
        <RotateCcw class="h-6 w-6" aria-hidden="true" />
        {t(msg`Try again`)}
      </button>
    </div>
  );
}
